import { Injectable } from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BfxRBACService } from "app/bfx-common/services/bfx-rbac.service";
import { BfxUserProfileService } from "app/bfx-common/services/bfx-user-profile.service";
import {
  BfxBlotterAction,
  ContextMenuItemState,
  Roles,
  BmarkFixingData,
  BmarkFixingType,
  ActiveUserProfilePromiseService,
  ActiveUser,
  BenchmarkDataLookupService,
  SystemConfigPromiseService,
  SysFeatures,
} from "../../../../../shared/cubiq-services";
import {
  BfxBaseBlotterContextMenuService,
  BfxBlotterActionEvent,
  BfxBlotterActionSubject,
  BfxLadderBlotterContextMenuEventType,
} from "../../../../bfx-base-blotter/bfx-base-blotter-contextmenu.service";
import { OEActionsService } from "app/order-entry/services/oe-actions.service";
import {
  BfxContextMenuItemMode,
  MODIFY_ALLOWED_STATES,
  CANCEL_ALLOWED_STATES,
  UNLOCK_ALLOWED_TYPES,
  UNLOCK_DISALLOWED_STATES,
  ACCEPT_REJECT_ALLOWED_STATES,
  //
  CLIENT_ORDERS_AVAILABLE_MODE_MAPPING,
  CLIENT_ORDERS_ENABLED_MODE_MAPPING,
  CLIENT_ORDERS_ENABLED_MUST_BE_PESSIMISTIC,
} from "../models/bmark-client-orders-context-menu.const";
import {
  BmarkClientOrdersContextMenuItem,
  BfxLadderBlotterClientOrdersContextMenuEvent,
} from "../models/bmark-client-orders-context-menu.models";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Injectable()
export class BmarkClientOrdersContextMenuService extends BfxBaseBlotterContextMenuService<any> {
  /**********************************************************************/
  /* DATA */

  public menuItems: Array<BmarkClientOrdersContextMenuItem>;

  protected ordersCount: number = 0;
  protected crossClient: boolean = false;
  protected riskRoutingEnabled: boolean = false;
  protected manualRiskRouting: boolean = false;
  protected modifyBenchmarksEnabled: boolean = false;

  protected user: {
    activeUser: ActiveUser;
    isTrader: boolean;
    isSales: boolean;
    isClient: boolean;
  } = {
    activeUser: null,
    isTrader: false,
    isSales: false,
    isClient: false,
  };

  protected permissions: {
    canEnterOrders: boolean;
    canViewOrderEntry: boolean;
    canConfirmTrade: boolean;
    canCancelTrade: boolean;
    canAddFillComment: boolean;
    canViewDetails: boolean;
    canViewTradeDetails: boolean;
    canExportTrade: boolean;
    canModifyOrder: boolean;
    canCancelOrder: boolean;
    canCancelCrossClient: boolean;
    canUnrestrictedCancelModify: boolean;
    canTraderAccept: boolean;
    canTraderReject: boolean;
    canAcceptRejectUnrestricted: boolean;
    canUnlockOrders: boolean;
    canTakeOrders: boolean;
    canTransferOrders: boolean;
    canTransferUnrestricted: boolean;
    canNet: boolean;
    canTransferAccept: boolean;
  } = {
    canEnterOrders: null,
    canViewOrderEntry: null,
    canConfirmTrade: null,
    canCancelTrade: null,
    canAddFillComment: null,
    canViewDetails: true,
    canViewTradeDetails: true,
    canExportTrade: true,
    canModifyOrder: false,
    canCancelOrder: false,
    canCancelCrossClient: false,
    canUnrestrictedCancelModify: false,
    canTraderAccept: false,
    canTraderReject: false,
    canAcceptRejectUnrestricted: false,
    canUnlockOrders: false,
    canTakeOrders: false,
    canTransferOrders: false,
    canTransferUnrestricted: false,
    canNet: false,
    canTransferAccept: false,
  };

  /**********************************************************************/
  /* LOGGING */

  LOG_PREFIX: string = "[BfxBmarkClientOrdersBlotterContextMenuService] ";
  TRACE_LOGGING: boolean = true;

  private traceLog(logString: string) {
    if (this.TRACE_LOGGING) logger.out.info(this.LOG_PREFIX + logString);
  }

  /**********************************************************************/
  /* CONSTRUCTOR */

  constructor(
    //private bmarkOrderDataLookupService: BenchmarkDataPrimaryLookupService,
    //private bmarkDataLookup: BenchmarkDataPrimaryLookupService,
    private bmarkDataLookup: BenchmarkDataLookupService,
    private rbacService: BfxRBACService,
    private userProfileService: BfxUserProfileService,
    private oeActions: OEActionsService,
    private activeUserService: ActiveUserProfilePromiseService,
    protected systemConfigService: SystemConfigPromiseService,
  ) {
    super(new BfxBlotterActionSubject());
    this.initializePermissions();
  }

  private initializePermissions() {
    // static permissions

    this.activeUserService.getActiveUserData().then(
      (activeUser: ActiveUser) => {
        this.user.activeUser = activeUser;
        this.user.isTrader = CubiqTS.UserType.TRADER === activeUser.userTypeId;
        this.user.isSales = CubiqTS.UserType.SALES === activeUser.userTypeId;
        this.user.isClient = CubiqTS.UserType.CLIENT === activeUser.userTypeId;
      },
      (reason) => {
        logger.out.error(`BmarkClientOrdersContextMenuService: ActiveUser ${reason}`);
      },
    );
    Promise.all([
      this.systemConfigService.getSystemConfigBoolean(SysFeatures.bmRiskRoutingEnabled),
      this.systemConfigService.getSystemConfigBoolean(SysFeatures.manualRiskRouting),
      this.systemConfigService.getSystemConfigBoolean(SysFeatures.benchmarkModificationEnable),
    ]).then(([riskRoutingEnabled, manualRiskRouting, modifyBenchmarksEnabled]) => {
      this.riskRoutingEnabled = riskRoutingEnabled;
      this.manualRiskRouting = manualRiskRouting;
      this.modifyBenchmarksEnabled = modifyBenchmarksEnabled;
    });

    // dynamic permissions
    Promise.all([
      this.rbacService.hasRole(Roles.ORDER_ENTRY),
      this.rbacService.hasRole(Roles.VIEW_ORDER_ENTRY),
      this.rbacService.hasRole(Roles.DEAL_ACK),
      this.rbacService.hasRole(Roles.DEAL_CANCEL),
      this.rbacService.hasRole(Roles.ADD_FILL_COMMENT),
      this.rbacService.hasRole(Roles.ORDER_MODIFY),
      this.rbacService.hasRole(Roles.ORDER_UNRESTRICTED_CANCEL_AND_MODIFY),
      this.rbacService.hasRole(Roles.ORDER_CANCEL_CROSS_CLIENT),
      this.rbacService.hasRole(Roles.ORDER_CANCEL),
      this.rbacService.hasRole(Roles.DI_TRADER_ACCEPT),
      this.rbacService.hasRole(Roles.DI_TRADER_REJECT),
      this.rbacService.hasRole(Roles.DI_TRADER_UNRESTRICTED),
      this.rbacService.hasRole(Roles.ORDER_UNLOCK),
      this.rbacService.hasRole(Roles.ORDER_STEAL_FROM_MANUAL),
      this.rbacService.hasRole(Roles.ORDER_TRANSFER),
      this.rbacService.hasRole(Roles.ORDER_TRANSFER_UNRESTRICTED),
      this.rbacService.hasRole(Roles.ROLE_ORDER_EXPORT),
      this.rbacService.hasRole(Roles.ROLE_ORDER_BENCHMARK_NETTING),
      this.rbacService.hasRole(Roles.DI_TRADER_TRANSFER),
    ]).then(
      ([
        canEnterOrders,
        canViewOrderEntry,
        canConfirmTrade,
        canCancelTrade,
        canAddFillComment,
        canModifyOrder,
        canCancelModifyUnrestricted,
        canCancelCross,
        canCancel,
        canTraderAccept,
        canTraderReject,
        canAcceptRejectUnrestricted,
        canUnlockOrders,
        canTakeOrders,
        canTransferOrders,
        canTransferUnrestriced,
        canExportTrade,
        canNet,
        diTraderTransfer,
      ]) => {
        this.permissions.canEnterOrders = canEnterOrders;
        this.permissions.canViewOrderEntry = canViewOrderEntry;
        this.permissions.canConfirmTrade = canConfirmTrade;
        this.permissions.canCancelTrade = canCancelTrade;
        this.permissions.canAddFillComment = canAddFillComment;
        // TODO - discuss role superset wtih product
        this.permissions.canModifyOrder = canModifyOrder || canCancelModifyUnrestricted;
        this.permissions.canTransferAccept = diTraderTransfer;
        if (canCancelModifyUnrestricted) {
          this.permissions.canUnrestrictedCancelModify = true;
          // implied by ordinal nature of role // role superset -dc
          this.permissions.canCancelCrossClient = true;
          // implied by ordinal nature of role // role superset -dc
          this.permissions.canCancelOrder = true;
        } else if (canCancelCross) {
          this.permissions.canUnrestrictedCancelModify = false;
          this.permissions.canCancelCrossClient = true;
          // implied by ordinal nature of role // role superset -dc
          this.permissions.canCancelOrder = true;
        } else if (canCancel) {
          this.permissions.canUnrestrictedCancelModify = false;
          this.permissions.canCancelCrossClient = false;
          this.permissions.canCancelOrder = true;
        }

        this.permissions.canTraderAccept = canTraderAccept;
        this.permissions.canTraderReject = canTraderReject;
        this.permissions.canAcceptRejectUnrestricted = canAcceptRejectUnrestricted;
        this.permissions.canUnlockOrders = canUnlockOrders;
        this.permissions.canTakeOrders = canTakeOrders;
        this.permissions.canTransferOrders = canTransferOrders;
        this.permissions.canTransferUnrestricted = canTransferUnrestriced;
        this.permissions.canExportTrade = canExportTrade;
        this.permissions.canNet = canNet;
        //this.initMenuItems();
      },
    );
  }

  /**********************************************************************/
  /* NOT USED - Inherited methods */

  protected getEnabledActions(
    items: Array<CubiqTS.OrderDataImpl>,
    selectedOrderIds: Array<string>,
  ): Array<BfxBlotterAction> {
    return [];
  }

  protected getActionDecorators(items: any[]) {
    // nothing to add
  }
  protected prepareActions(ladderBlotter: any): void {
    // nothing to add
  }

  /**********************************************************************/
  /* METHODS */
  public getMenuItems(): any[] {
    return this.menuItems;
  }

  protected createMenuItems(): Array<BmarkClientOrdersContextMenuItem> {
    let menuItems: BmarkClientOrdersContextMenuItem[] = [];
    menuItems.push(
      new BmarkClientOrdersContextMenuItem(BfxBlotterAction.ACTION_ORDER_ACCEPT, "Accept"),
    );
    menuItems.push(
      new BmarkClientOrdersContextMenuItem(BfxBlotterAction.ACTION_ORDER_REJECT, "Reject"),
    );
    menuItems.push(
      new BmarkClientOrdersContextMenuItem(BfxBlotterAction.SEPARATOR, "Accept Separator"),
    );
    menuItems.push(
      new BmarkClientOrdersContextMenuItem(BfxBlotterAction.ACTION_ORDER_MODIFY, "Modify"),
    );
    menuItems.push(
      new BmarkClientOrdersContextMenuItem(
        BfxBlotterAction.ACTION_ORDER_CANCEL,
        "Cancel",
        "icon-menu-error",
      ),
    );
    menuItems.push(
      new BmarkClientOrdersContextMenuItem(BfxBlotterAction.ACTION_ORDER_DUPLICATE, "Duplicate"),
    );
    menuItems.push(
      new BmarkClientOrdersContextMenuItem(BfxBlotterAction.ACTION_ORDER_UNLOCK, "Unlock"),
    );
    menuItems.push(
      new BmarkClientOrdersContextMenuItem(BfxBlotterAction.ACTION_TRANSFER, "Transfer"),
    );
    menuItems.push(new BmarkClientOrdersContextMenuItem(BfxBlotterAction.ACTION_TAKE, "Take"));
    menuItems.push(
      new BmarkClientOrdersContextMenuItem(BfxBlotterAction.ACTION_VIEW_DETAILS, "View Order"),
    );
    menuItems.push(
      new BmarkClientOrdersContextMenuItem(
        BfxBlotterAction.ACTION_TRADE_VIEW_DETAILS,
        "View Trade Details",
      ),
    );
    menuItems.push(new BmarkClientOrdersContextMenuItem(BfxBlotterAction.SEPARATOR));
    menuItems.push(
      new BmarkClientOrdersContextMenuItem(BfxBlotterAction.ACTION_NET_ORDERS, "Net Orders"),
    );
    menuItems.push(
      new BmarkClientOrdersContextMenuItem(BfxBlotterAction.ACTION_NET_CLEAR, "Clear Netting"),
    );
    menuItems.push(
      new BmarkClientOrdersContextMenuItem(BfxBlotterAction.ACTION_NET_DISABLE, "Disable Netting"),
    );

    menuItems.push(new BmarkClientOrdersContextMenuItem(BfxBlotterAction.SEPARATOR));
    menuItems.push(
      new BmarkClientOrdersContextMenuItem(BfxBlotterAction.ACTION_TRADE_EXPORT, "Export to CSV"),
    );
    return menuItems;
  }

  public buildMenu(
    blotter: any, //import('../../../../bfx-base-blotter/bfx-base-blotter').BfxBaseBlotter,
    pageX: number,
    pageY: number,
    selectedIds: string[],
    selectedItems?: Array<CubiqTS.OrderDataImpl> | null,
  ): void {
    if (selectedItems == null) {
      selectedItems = this.bmarkDataLookup.getBmarkOrderDataForOrderIds(selectedIds, true, true);
      this.setSelectedItems(selectedItems, blotter);
    }

    const orderIds: Set<string> = new Set();
    for (let selectedId of selectedIds) {
      orderIds.add(selectedId);
    }
    let menuItems = this.createMenuItems();
    if (Array.isArray(selectedItems) && selectedItems.length > 0) {
      this.ordersCount = selectedItems.length;
      this.crossClient = this.isCrossClient(selectedItems);
      for (let selectedItem of selectedItems) {
        for (let menuItem of menuItems) {
          switch (menuItem.actionId) {
            case BfxBlotterAction.ACTION_ORDER_ACCEPT:
              let acceptAvailableEnabled = this.checkTraderAcceptAvailableEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, acceptAvailableEnabled);
              this.applySelectedItemToMenuItem(
                menuItem,
                acceptAvailableEnabled,
                acceptAvailableEnabled,
              );
              break;
            case BfxBlotterAction.ACTION_ORDER_REJECT:
              let traderRejectAvailableEnabled =
                this.checkTraderRejectAvailableEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, traderRejectAvailableEnabled);
              this.applySelectedItemToMenuItem(
                menuItem,
                traderRejectAvailableEnabled,
                traderRejectAvailableEnabled,
              );
              break;
            case BfxBlotterAction.ACTION_ORDER_MODIFY:
              let modifyAvailable =
                this.permissions.canModifyOrder &&
                this.permissions.canViewOrderEntry &&
                this.modifyBenchmarksEnabled;
              let modifyEnabled = this.checkModifyEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, modifyEnabled);
              this.applySelectedItemToMenuItem(menuItem, modifyAvailable, modifyEnabled);
              break;
            case BfxBlotterAction.ACTION_ORDER_CANCEL:
              let cancelAvailable = true;
              let cancelEnabled = this.checkCancelEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, cancelEnabled);
              this.applySelectedItemToMenuItem(menuItem, cancelAvailable, cancelEnabled);
              break;
            case BfxBlotterAction.ACTION_ORDER_DUPLICATE: // not handled
              let duplicateAvailable =
                this.permissions.canEnterOrders && this.permissions.canViewOrderEntry;
              let duplicateEnabled = this.checkDuplicateEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, duplicateEnabled);
              this.applySelectedItemToMenuItem(menuItem, duplicateAvailable, duplicateEnabled);
              break;
            case BfxBlotterAction.ACTION_ORDER_UNLOCK:
              let unlockAvailable = true;
              let unlockEnabled = this.checkUnlockEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, unlockEnabled);
              this.applySelectedItemToMenuItem(menuItem, unlockAvailable, unlockEnabled);
              break;
            case BfxBlotterAction.ACTION_TRANSFER:
              let transferAvailable = this.permissions.canTransferOrders;
              let transferEnabled = this.checkTransferEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, transferEnabled);
              this.applySelectedItemToMenuItem(menuItem, transferAvailable, transferEnabled);
              break;
            case BfxBlotterAction.ACTION_TAKE:
              let takeAvailable = this.permissions.canTakeOrders;
              let takeEnabled = this.checkTakeEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, takeEnabled);
              this.applySelectedItemToMenuItem(menuItem, takeAvailable, takeEnabled);
              break;
            case BfxBlotterAction.ACTION_VIEW_DETAILS:
              let viewDetailsAvailable = true;
              let viewDetailsEnabled = this.checkViewDetailsEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, viewDetailsEnabled);
              this.applySelectedItemToMenuItem(menuItem, viewDetailsAvailable, viewDetailsEnabled);
              break;
            case BfxBlotterAction.ACTION_TRADE_VIEW_TRADE_DETAILS:
              let viewTradeAvailable = true;
              let viewTradeEnabled = this.checkViewTradeEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, viewTradeEnabled);
              this.applySelectedItemToMenuItem(menuItem, viewTradeAvailable, viewTradeEnabled);
              break;
            case BfxBlotterAction.ACTION_TRADE_EXPORT:
              let tradeExportAvailable = this.permissions.canExportTrade;
              let tradeExportEnabled = this.checkExportTradeEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, tradeExportEnabled);
              this.applySelectedItemToMenuItem(menuItem, tradeExportAvailable, tradeExportEnabled);
              break;
            case BfxBlotterAction.SEPARATOR:
              menuItem.state.available = true;
              menuItem.state.enabled = true;
              break;
            case BfxBlotterAction.ACTION_NET_CLEAR:
              {
                const netAvailable = this.permissions.canNet;
                const netEnabled =
                  this.checkNettingEnabled(selectedItem) && selectedItem.fwdNettingId != null;
                this.addReferenceIdToMenuItem(menuItem, selectedItem, netEnabled);
                this.applySelectedItemToMenuItem(menuItem, netAvailable, netEnabled);
              }
              break;
            case BfxBlotterAction.ACTION_NET_DISABLE:
              {
                const netAvailable = this.permissions.canNet;
                const netEnabled = this.checkNettingEnabled(selectedItem);
                this.addReferenceIdToMenuItem(menuItem, selectedItem, netEnabled);
                this.applySelectedItemToMenuItem(menuItem, netAvailable, netEnabled);
              }
              break;
            case BfxBlotterAction.ACTION_NET_ORDERS:
              {
                const netAvailable = this.permissions.canNet;
                const netEnabled = this.checkNettingEnabled(selectedItem) && orderIds.size > 1;
                this.addReferenceIdToMenuItem(menuItem, selectedItem, netEnabled);
                this.applySelectedItemToMenuItem(menuItem, netAvailable, netEnabled);
              }
              break;
            default:
              break;
          }
        }
      }
    }

    menuItems = this.reduceVisibleMenuItems(menuItems, selectedIds, selectedItems);

    this.showMenuSubject.next(
      new BfxLadderBlotterClientOrdersContextMenuEvent(
        BfxLadderBlotterContextMenuEventType.MENU_SHOW,
        blotter,
        pageX,
        pageY,
        [],
        null,
        Array.from(menuItems.values()),
      ),
    );
  }

  public fireActionEvent(
    actionId: BfxBlotterAction,
    ladderBlotter: any,
    selectedIds?: Array<string>,
    selectedItems?: Array<any>,
    itemState?: ContextMenuItemState,
    hide?: boolean,
    menuBoundingClientRect?: any,
    actionEvent?: any,
  ): void {
    let event: BfxBlotterActionEvent = new BfxBlotterActionEvent(
      actionId,
      ladderBlotter,
      this.getSelectedIds(selectedIds, ladderBlotter),
      this.getSelectedItems(selectedItems, ladderBlotter),
      itemState,
      hide,
      menuBoundingClientRect,
      actionEvent,
    );
    this.actionSubject.next(event);
  }

  /**********************************************************************/
  /* METHODS */

  protected applySelectedItemToMenuItem(
    menuItem: BmarkClientOrdersContextMenuItem,
    itemAvailable: boolean,
    itemEnabled: boolean,
    mapActionId: BfxBlotterAction | string = null,
  ): void {
    mapActionId = mapActionId != null ? mapActionId : menuItem.actionId;
    let availableMode = CLIENT_ORDERS_AVAILABLE_MODE_MAPPING.get(mapActionId);
    let enabledMode = CLIENT_ORDERS_ENABLED_MUST_BE_PESSIMISTIC.includes(mapActionId)
      ? BfxContextMenuItemMode.PESSIMISTIC
      : CLIENT_ORDERS_ENABLED_MODE_MAPPING.get(mapActionId);
    menuItem.state.available =
      menuItem.state.available == null
        ? itemAvailable
        : availableMode === BfxContextMenuItemMode.PESSIMISTIC
          ? menuItem.state.available && itemAvailable
          : menuItem.state.available || itemAvailable;
    menuItem.state.enabled =
      menuItem.state.enabled == null
        ? itemEnabled
        : enabledMode === BfxContextMenuItemMode.PESSIMISTIC
          ? menuItem.state.enabled && itemEnabled
          : menuItem.state.enabled || itemEnabled;
  }

  protected addReferenceIdToMenuItem(
    menuItem: BmarkClientOrdersContextMenuItem,
    orderData: CubiqTS.OrderDataImpl,
    itemEnabled: boolean,
  ): void {
    if (
      menuItem instanceof BmarkClientOrdersContextMenuItem &&
      orderData instanceof CubiqTS.OrderDataImpl &&
      typeof itemEnabled === "boolean" &&
      itemEnabled
    ) {
      switch (menuItem.actionId) {
        case BfxBlotterAction.ACTION_ORDER_ACCEPT:
        case BfxBlotterAction.ACTION_ORDER_REJECT:
          let acceptanceType = this.determineAcceptanceType(orderData);
          if (this.hasOrderId(orderData) && acceptanceType != null) {
            if (
              menuItem.state.acceptanceReference.has(acceptanceType) &&
              Array.isArray(menuItem.state.acceptanceReference.get(acceptanceType))
            ) {
              menuItem.state.acceptanceReference
                .get(acceptanceType)
                .push(this.getOrderId(orderData));
            } else {
              menuItem.state.acceptanceReference.set(acceptanceType, [this.getOrderId(orderData)]);
            }
          }
          break;
        default:
          if (this.hasOrderId(orderData)) {
            menuItem.state.referencedOrderIds.push(this.getOrderId(orderData));
          }
          break;
      }
    }
  }

  /**********************************************************************/
  /* METHODS - ACTION CONDITIONS */

  /* ACCEPT (Available/Enabled) */
  protected checkTraderAcceptAvailableEnabled(orderData: CubiqTS.OrderDataImpl): boolean {
    return (
      this.isBenchmark(orderData) &&
      ACCEPT_REJECT_ALLOWED_STATES.includes(orderData.orderState) &&
      ((this.user.isTrader &&
        this.permissions.canTraderAccept &&
        this.isTraderInGroup(orderData)) ||
        (this.permissions.canAcceptRejectUnrestricted && !this.user.isClient))
    );
  }

  /* REJECT (Available/Enabled) */
  protected checkTraderRejectAvailableEnabled(orderData: CubiqTS.OrderDataImpl): boolean {
    return (
      this.isBenchmark(orderData) &&
      ACCEPT_REJECT_ALLOWED_STATES.includes(orderData.orderState) &&
      ((this.user.isTrader &&
        this.permissions.canTraderReject &&
        this.isTraderInGroup(orderData)) ||
        (this.permissions.canAcceptRejectUnrestricted && !this.user.isClient))
    );
  }

  /* MODIFY (Enabled) */
  public checkModifyEnabled(orderData: CubiqTS.OrderDataImpl): boolean {
    return (
      this.permissions.canModifyOrder &&
      this.ordersCount === 1 &&
      this.isBenchmark(orderData) &&
      this.permissions.canViewOrderEntry &&
      MODIFY_ALLOWED_STATES.includes(orderData.orderState) &&
      this.isInActiveFixing(orderData) &&
      !this.isInHCOFixing(orderData) &&
      !this.isPostFixing(orderData) &&
      (!this.isLocked(orderData) || this.isMyLock(orderData))
    );
  }

  /* UNLOCK (Enabled) */
  protected checkUnlockEnabled(orderData: CubiqTS.OrderDataImpl): boolean {
    return (
      this.permissions.canUnlockOrders &&
      this.ordersCount === 1 &&
      this.isBenchmark(orderData) &&
      this.isLocked(orderData) &&
      UNLOCK_ALLOWED_TYPES.includes(orderData.lockType) &&
      !UNLOCK_DISALLOWED_STATES.includes(orderData.orderState)
    );
  }

  /* CANCEL (Enabled) */
  protected checkCancelEnabled(orderData: CubiqTS.OrderDataImpl): boolean {
    return (
      this.permissions.canCancelOrder &&
      this.isBenchmark(orderData) &&
      CANCEL_ALLOWED_STATES.includes(orderData.orderState) && // not canceled
      (!this.user.isSales ||
        this.isSalesInGroup(orderData) ||
        this.permissions.canUnrestrictedCancelModify) &&
      (!this.user.isTrader ||
        this.isTraderInGroup(orderData) ||
        this.permissions.canUnrestrictedCancelModify) &&
      this.isInActiveFixing(orderData) &&
      !this.isInHCOFixing(orderData) &&
      !this.isPostFixing(orderData) &&
      (this.permissions.canCancelCrossClient || !this.crossClient)
    );
  }

  /* DUPLICATE (Enabled) */
  protected checkDuplicateEnabled(orderData: CubiqTS.OrderDataImpl): boolean {
    return (
      this.isBenchmark(orderData) &&
      this.permissions.canEnterOrders &&
      this.permissions.canViewOrderEntry &&
      this.ordersCount === 1
    );
  }

  /* VIEW DETAILS (Enabled) */
  protected checkViewDetailsEnabled(orderData: CubiqTS.OrderDataImpl): boolean {
    return (
      this.permissions.canViewTradeDetails && this.ordersCount === 1 && this.isBenchmark(orderData)
    );
  }

  /* VIEW TRADE (Enabled) */
  protected checkViewTradeEnabled(orderData: CubiqTS.OrderDataImpl): boolean {
    return (
      this.permissions.canViewTradeDetails &&
      this.ordersCount === 1 &&
      this.isBenchmark(orderData) &&
      this.isFilled(orderData)
    );
  }

  /* TRANSFER (Enabled) */
  public checkTransferEnabled(orderData: CubiqTS.OrderDataImpl): boolean {
    return (
      this.isClientBenchmark(orderData) &&
      ((this.user.isTrader &&
        this.permissions.canTransferOrders &&
        this.isTraderInGroup(orderData, true)) ||
        this.permissions.canTransferUnrestricted) &&
      orderData.orderState == CubiqTS.OrderState.ACTIVE &&
      orderData.lockUserId == null &&
      (!this.isInHCOFixing(orderData) || !this.riskRoutingEnabled || this.manualRiskRouting) &&
      !this.isPostFixing(orderData)
    );
  }

  /* TAKE (Enabled) */
  public checkTakeEnabled(orderData: CubiqTS.OrderDataImpl): boolean {
    return (
      this.permissions.canTakeOrders &&
      this.isClientBenchmark(orderData) &&
      this.user.isTrader &&
      orderData.orderState == CubiqTS.OrderState.ACTIVE &&
      orderData.lockUserId == null &&
      (!this.isInHCOFixing(orderData) || !this.riskRoutingEnabled || this.manualRiskRouting) &&
      !this.isPostFixing(orderData)
    );
  }

  /* EXPORT TRADE (Enabled) */
  protected checkExportTradeEnabled(orderData: CubiqTS.OrderDataImpl): boolean {
    return this.permissions.canExportTrade && this.ordersCount > 0;
  }

  /**
   * Check to see if netting is available for an order - in fact, if this returns true for any seleted order
   * then it will be true for every order but we keep it on per-order basis for moment
   * @param orderData
   * @returns
   */
  protected checkNettingEnabled(orderData: CubiqTS.OrderDataImpl): boolean {
    return (
      this.permissions.canNet &&
      this.isBenchmark(orderData) &&
      this.isInActiveFixing(orderData) &&
      !this.isInHCOFixing(orderData) &&
      !this.isPostFixing(orderData)
    );
  }

  /* ******************************************************* */
  /* METHODS - ORDER DATA HELPERS  */
  private isBenchmark(orderData: CubiqTS.OrderDataImpl): boolean {
    return (
      orderData instanceof CubiqTS.OrderDataImpl &&
      orderData.orderType === CubiqTS.OrderType.BENCHMARK
    );
  }

  private isClientBenchmark(orderData: CubiqTS.OrderDataImpl): boolean {
    return (
      this.isBenchmark(orderData) &&
      orderData.fixingDetails != null &&
      orderData.fixingDetails.bmOrderType == CubiqTS.BenchmarkOrderType.Standard
    );
  }

  private isFilled(orderData: CubiqTS.OrderDataImpl): boolean {
    return (
      orderData instanceof CubiqTS.OrderDataImpl &&
      orderData.orderState === CubiqTS.OrderState.FILLED
    );
  }

  private isLocked(orderData: CubiqTS.OrderDataImpl): boolean {
    return orderData instanceof CubiqTS.OrderDataImpl && orderData.lockUserId != null;
  }

  private isMyLock(orderData: CubiqTS.OrderDataImpl): boolean {
    return orderData instanceof CubiqTS.OrderDataImpl && orderData.lockUserId != null
      ? orderData.lockUserId.toString() === this.user.activeUser.userId
      : false;
  }
  private isMyClient(orderData: CubiqTS.OrderDataImpl): boolean {
    return (
      this.user.isClient &&
      orderData instanceof CubiqTS.OrderDataImpl &&
      this.user.activeUser.clientCode === orderData.clientCode
    );
  }

  private getOrderId(orderData: CubiqTS.OrderDataImpl): string {
    return this.hasOrderId(orderData) ? orderData.orderId.toString() : null;
  }

  private hasOrderId(orderData: CubiqTS.OrderDataImpl): boolean {
    return orderData instanceof CubiqTS.OrderDataImpl && orderData.orderId instanceof CubiqTS.Long;
  }

  private hasOrderData(orderData: CubiqTS.OrderDataImpl): boolean {
    return orderData instanceof CubiqTS.OrderDataImpl;
  }

  private determineAcceptanceType(orderData: CubiqTS.OrderDataImpl): CubiqTS.AcceptanceType {
    let acceptanceType: CubiqTS.AcceptanceType = null;
    if (this.hasOrderData(orderData) && orderData.orderState in CubiqTS.OrderState) {
      if (orderData.orderState == CubiqTS.OrderState.ACCEPTANCE_NEEDED) {
        if (orderData.lockType == CubiqTS.LockType.MODIFY_LOCK) {
          acceptanceType = CubiqTS.AcceptanceType.MODIFICATION;
        } else {
          acceptanceType = CubiqTS.AcceptanceType.ENTRY;
        }
      } else if (orderData.orderState == CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED) {
        acceptanceType = CubiqTS.AcceptanceType.MODIFICATION;
      } else if (orderData.orderState == CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED) {
        acceptanceType = CubiqTS.AcceptanceType.CANCEL;
      }
    }
    return acceptanceType;
  }

  private isSalesInGroup(orderData: CubiqTS.OrderDataImpl): boolean {
    return (
      this.user.isSales &&
      this.hasOrderData(orderData) &&
      this.user.activeUser.salesGroupsIdSet.has(orderData.salesGroupId.toString())
    );
  }

  private isTraderInGroup(orderData: CubiqTS.OrderDataImpl, checkRisks: boolean = false): boolean {
    if (orderData instanceof CubiqTS.OrderDataImpl) {
      if (this.isBenchmark(orderData)) {
        if (
          checkRisks &&
          (orderData.allocationData[0].allocationType == CubiqTS.AllocationType.COMPONENT ||
            orderData.allocationData[0].allocationType == CubiqTS.AllocationType.UNSPAWNEDCOMPONENT)
        ) {
          return (
            this.user.isTrader &&
            this.user.activeUser.tradingGroupsIdSet.has(
              orderData.allocationData[0].deskId.toString(),
            )
          );
        } else {
          let traderGroupIds: Array<string> = Array.isArray(orderData.ownerGroupIds)
            ? orderData.ownerGroupIds.map((groupId) => groupId.toString())
            : null;
          if (Array.isArray(traderGroupIds) && traderGroupIds.length > 0) {
            let hasGroupMembership: boolean = false;
            for (let traderGroupId of traderGroupIds) {
              hasGroupMembership =
                hasGroupMembership || this.user.activeUser.tradingGroupsIdSet.has(traderGroupId);
            }
            return this.user.isTrader && hasGroupMembership;
          }
        }
      } else {
        let deskId =
          (orderData as CubiqTS.OrderDataImpl).deskId != null
            ? (orderData as CubiqTS.OrderDataImpl).deskId.toString()
            : null;
        return (
          CubiqTS.UserType.TRADER === this.user.activeUser.userTypeId &&
          this.user.activeUser.tradingGroupsIdSet.has(deskId)
        );
      }
    }
    return false;
  }

  private isInActiveFixing(orderData: CubiqTS.OrderDataImpl): boolean {
    let activeFixingId: string = BmarkFixingData.generateFixingIdFromOrderData(
      orderData,
      BmarkFixingType.ACTIVE,
    );
    let fixingData: BmarkFixingData = this.bmarkDataLookup.getActiveFixingData(activeFixingId);
    return fixingData instanceof BmarkFixingData;
  }

  private isInHCOFixing(orderData: CubiqTS.OrderDataImpl): boolean {
    let activeFixingId: string = BmarkFixingData.generateFixingIdFromOrderData(
      orderData,
      BmarkFixingType.ACTIVE,
    );
    let fixingData: BmarkFixingData = this.bmarkDataLookup.getActiveFixingData(activeFixingId);
    return (
      fixingData instanceof BmarkFixingData && fixingData.state === CubiqTS.FixingState.HARD_CUTOFF
    );
  }

  private isPostFixing(orderData: CubiqTS.OrderDataImpl): boolean {
    let activeFixingId: string = BmarkFixingData.generateFixingIdFromOrderData(
      orderData,
      BmarkFixingType.ACTIVE,
    );
    let fixingData: BmarkFixingData = this.bmarkDataLookup.getActiveFixingData(activeFixingId);
    return (
      fixingData instanceof BmarkFixingData &&
      (fixingData.state === CubiqTS.FixingState.FIXING_HIT ||
        fixingData.state === CubiqTS.FixingState.TRIGGERED ||
        fixingData.state === CubiqTS.FixingState.COMPLETED)
    );
  }

  /* METHODS - ORDER DATASET HELPERS  */
  private isCrossClient(orderDataset: Array<CubiqTS.OrderDataImpl>): boolean {
    if (Array.isArray(orderDataset) && orderDataset.length > 0) {
      if (orderDataset.length === 1) {
        return false;
      } else {
        let baselineClientCode = this.hasOrderData(orderDataset[0])
          ? orderDataset[0].clientCode
          : null;
        for (let orderData of orderDataset) {
          if (orderData.clientCode !== baselineClientCode) {
            return true;
          }
        }
      }
    }
    return false;
  }
}
